import type { ProblemResponse } from '@emr/shared';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { listProblems } from '../../../api/chart.ts';
import type { DataTableColumn } from '../../../components/DataTable.tsx';
import { formatCalendarDate } from '../../../lib/dateFormat.ts';
import { ChartTable } from './ChartTable.tsx';

function columnsFor(t: TFunction<'patientChartTabs'>): readonly DataTableColumn<ProblemResponse>[] {
  return [
    {
      key: 'description',
      header: t('problems.columns.description'),
      cell: (problem) => problem.description,
      wrap: 'anywhere',
      maxWidthCh: 40,
    },
    {
      key: 'icd10',
      header: t('problems.columns.icd10'),
      cell: (problem) => problem.icd10Code ?? '—',
      wrap: 'nowrap',
      cellClassName: 'font-mono',
    },
    // `status` is open free text (`optionalTrimmed(MAX_NAME_LENGTH)`), not a closed enum, so it
    // gets the same wrap+cap treatment as `description` rather than `nowrap` — see `DataTable.tsx`'s
    // own header comment on why the wrap class alone does not constrain a table column by itself.
    {
      key: 'status',
      header: t('problems.columns.status'),
      cell: (problem) => problem.status,
      wrap: 'wrap',
      maxWidthCh: 14,
    },
    {
      key: 'onset',
      header: t('problems.columns.onset'),
      cell: (problem) => (problem.onsetDate === null ? '—' : formatCalendarDate(problem.onsetDate)),
      wrap: 'nowrap',
    },
  ];
}

export function ProblemsTab({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChartTabs');
  return (
    <ChartTable
      queryKey={['patient', patientId, 'problems']}
      queryFn={(signal) => listProblems(patientId, signal)}
      columns={columnsFor(t)}
      rowKey={(problem) => problem.id}
      minWidthRem={44}
      emptyMessage={t('problems.empty')}
      loadingLabel={t('problems.loading')}
      caption={t('problems.caption')}
    />
  );
}
